import { BlogArticle } from '../types';

// PDF 講義整理筆記 → CTA: /module/notes
const article: BlogArticle = {
  slug: 'turn-pdf-handouts-into-study-notes',
  date: '2026-09-03',
  readingMinutes: 4,
  tags: ['筆記', 'PDF', '溫書技巧'],

  locales: {
    zhHant: {
      title: 'PDF 講義變筆記：3 步整理法，溫書效率翻倍',
      excerpt: '成堆 PDF 講義唔知點溫？直接重讀講義係最低效嘅做法。學識呢 3 步，將講義變成真正屬於你嘅筆記。',
      keywords: 'PDF筆記, 筆記整理, 講義, 溫書方法, study notes',
      contentHtml: `
        <p>每個學期都儲落一堆 PDF 講義，但到考試先發現：由頭到尾再讀一次，讀完都係唔記得。原因係 —— <strong>講義係老師嘅思路，唔係你嘅記憶</strong>。要真正吸收，一定要將佢轉化成你自己嘅筆記。</p>

        <h2>第 1 步：提取重點（唔好齋 highlight）</h2>
        <p>Highlight 成版黃色等於冇 highlight。正確做法係：每讀完一個小節，<strong>合上講義，用自己嘅字寫低 3 個重點</strong>。寫唔出 = 未明，即刻返去重讀。</p>

        <h2>第 2 步：重組架構</h2>
        <p>講義嘅次序係教學次序，未必係你溫習嘅最佳次序。試吓用以下方式重組：</p>
        <ul>
          <li><strong>概念圖</strong>：將概念之間嘅關係畫出嚟</li>
          <li><strong>比較表</strong>：易混淆嘅概念並排比較（例如 mitosis vs meiosis）</li>
          <li><strong>問答形式</strong>：將重點改寫成問題，方便之後自我測驗</li>
        </ul>

        <div class="tip-box">
          <strong>💡 貼士：</strong>「問答形式」嘅筆記最值錢 —— 因為佢直接變成你嘅自我測驗材料，一邊溫一邊考自己，效果遠勝重讀。
        </div>

        <h2>第 3 步：自問自答</h2>
        <p>筆記整理好之後，唔好放埋一邊。隔幾日攞返出嚟，遮住答案回答自己寫嘅問題。答唔出嘅，就係你要重溫嘅位。</p>

        <h2>懶人做法：AI 幫你整理</h2>
        <p>如果講義太多整理唔切，可以直接上載 PDF 俾 AI：佢會自動提取重點、整理成結構化筆記，仲可以生成問答方便你自測：</p>
        <p><a class="blog-cta-button" href="/module/notes">📝 免費將 PDF 變成筆記 →</a></p>

        <h2>整理前後對比</h2>
        <table>
          <thead>
            <tr><th>❌ 直接讀講義</th><th>✅ 整理成筆記</th></tr>
          </thead>
          <tbody>
            <tr><td>被動接收，好快唔記得</td><td>主動加工，記憶深刻</td></tr>
            <tr><td>次次由頭讀起</td><td>只溫唔熟嘅問題</td></tr>
            <tr><td>唔知自己邊度唔明</td><td>自問自答即刻現形</td></tr>
          </tbody>
        </table>

        <blockquote>筆記嘅價值唔在於「記低咗」，而在於整理嘅過程令你真正理解。</blockquote>
      `,
    },

    zhHans: {
      title: 'PDF 讲义变笔记：3 步整理法，复习效率翻倍',
      excerpt: '一堆 PDF 讲义不知道怎么复习？直接重读讲义是最低效的做法。学会这 3 步，把讲义变成真正属于你的笔记。',
      keywords: 'PDF笔记, 笔记整理, 讲义, 复习方法, study notes',
      contentHtml: `
        <p>每个学期都攒下一堆 PDF 讲义，但到考试才发现：从头到尾再读一遍，读完还是记不住。原因是 —— <strong>讲义是老师的思路，不是你的记忆</strong>。要真正吸收，一定要把它转化成你自己的笔记。</p>

        <h2>第 1 步：提取重点（不要只 highlight）</h2>
        <p>Highlight 整版黄色等于没 highlight。正确做法是：每读完一个小节，<strong>合上讲义，用自己的话写下 3 个重点</strong>。写不出 = 没懂，立刻回去重读。</p>

        <h2>第 2 步：重组架构</h2>
        <p>讲义的次序是教学次序，未必是你复习的最佳次序。试试用以下方式重组：</p>
        <ul>
          <li><strong>概念图</strong>：把概念之间的关系画出来</li>
          <li><strong>比较表</strong>：易混淆的概念并排比较（例如 mitosis vs meiosis）</li>
          <li><strong>问答形式</strong>：把重点改写成问题，方便之后自我测验</li>
        </ul>

        <div class="tip-box">
          <strong>💡 提示：</strong>「问答形式」的笔记最值钱 —— 因为它直接变成你的自我测验材料，一边复习一边考自己，效果远胜重读。
        </div>

        <h2>第 3 步：自问自答</h2>
        <p>笔记整理好之后，不要放一边。隔几天拿出来，遮住答案回答自己写的问题。答不出的，就是你要重温的地方。</p>

        <h2>懒人做法：AI 帮你整理</h2>
        <p>如果讲义太多整理不完，可以直接上传 PDF 给 AI：它会自动提取重点、整理成结构化笔记，还可以生成问答方便你自测：</p>
        <p><a class="blog-cta-button" href="/module/notes">📝 免费将 PDF 变成笔记 →</a></p>

        <h2>整理前后对比</h2>
        <table>
          <thead>
            <tr><th>❌ 直接读讲义</th><th>✅ 整理成笔记</th></tr>
          </thead>
          <tbody>
            <tr><td>被动接收，很快忘记</td><td>主动加工，记忆深刻</td></tr>
            <tr><td>次次从头读起</td><td>只复习不熟的问题</td></tr>
            <tr><td>不知道自己哪里不懂</td><td>自问自答立刻现形</td></tr>
          </tbody>
        </table>

        <blockquote>笔记的价值不在于「记下了」，而在于整理的过程让你真正理解。</blockquote>
      `,
    },

    en: {
      title: 'Turn PDF Handouts into Study Notes: A 3-Step Method',
      excerpt: "Drowning in PDF handouts? Re-reading them is the least effective way to study. These 3 steps turn handouts into notes that are actually yours.",
      keywords: 'PDF notes, study notes, note taking, study methods, revision',
      contentHtml: `
        <p>Every semester piles up a stack of PDF handouts — and come exam time, re-reading them cover to cover somehow leaves nothing behind. The reason: <strong>a handout is your teacher's thinking, not your memory</strong>. To actually absorb it, you have to transform it into your own notes.</p>

        <h2>Step 1: Extract the key points (don't just highlight)</h2>
        <p>A page drowned in yellow highlighter is the same as no highlight at all. Instead: after each section, <strong>close the handout and write down 3 key points in your own words</strong>. Can't write them = you don't understand it yet. Go back immediately.</p>

        <h2>Step 2: Restructure</h2>
        <p>A handout's order is a teaching order — not necessarily your best revision order. Try restructuring with:</p>
        <ul>
          <li><strong>Concept maps</strong>: draw how ideas connect</li>
          <li><strong>Comparison tables</strong>: line up easily-confused concepts (e.g. mitosis vs meiosis)</li>
          <li><strong>Q&A format</strong>: rewrite key points as questions for later self-testing</li>
        </ul>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Q&A-format notes are the most valuable — they become ready-made self-tests. Testing yourself while you revise beats re-reading every time.
        </div>

        <h2>Step 3: Quiz yourself</h2>
        <p>Don't file your notes away. A few days later, pull them out, cover the answers, and respond to your own questions. Whatever you can't answer is exactly what you need to review.</p>

        <h2>The lazy way: let AI do it</h2>
        <p>Too many handouts to process? Upload the PDF and let AI extract the key points into structured notes — it can even generate Q&A for self-testing:</p>
        <p><a class="blog-cta-button" href="/module/notes">📝 Turn a PDF into notes for free →</a></p>

        <h2>Before vs after</h2>
        <table>
          <thead>
            <tr><th>❌ Re-reading handouts</th><th>✅ Structured notes</th></tr>
          </thead>
          <tbody>
            <tr><td>Passive intake, forgotten fast</td><td>Active processing, deep memory</td></tr>
            <tr><td>Always start from page 1</td><td>Review only what you're weak at</td></tr>
            <tr><td>No idea where the gaps are</td><td>Self-quizzing exposes them instantly</td></tr>
          </tbody>
        </table>

        <blockquote>The value of notes isn't what you wrote down — it's the understanding built while organising it.</blockquote>
      `,
    },
  },
};

export default article;
